import { Box, Card, Grid, Skeleton, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import emptySensorImage from 'assets/images/sensor1.png';
import CustomSnackbar from 'components/CustomSnackbar';
import DevicesListFilter from 'components/DevicesFilter/DevicesListFilter.js';
import LiveClock from 'components/LiveClock/LiveClock.js';
import DevicesCount from 'components/DevicesCount/DevicesCount.js';
import MDTypography from 'components/MDTypography/index';
import { useEffect, useRef, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SensorCard } from './SensorCard.js';
import { fetchUserDevices } from 'stores/sensor/sensorStore';
import { filterDevices } from '../../helpers/filterHelpers';
import { setCurrentPage, clearSearchString } from '../../stores/sensor/sensorStore';
import { brandColors } from 'types/colors';

const REFRESH_TIME_IN_SECONDS = 300;
const SKELETON_CARDS_COUNT = 8;

export const SensorsList = ({ setPdfName }) => {
  const dispatch = useDispatch();
  // const navigate = useNavigate();
  const currentUser = useSelector(state => state.sensors.currentUser);

  const { selectedFilterObject } = useSelector(state => state.persistence);
  // const searchString = useSelector(state => state.persistence.searchString || '');
  const allDevices = useSelector(state => state.sensors.devices);
  const devices = useSelector(state => state.sensors.filteredDevices);
  const isLoading = useSelector(state => state.sensors.loading);

  const { distributor, customer, building, floor, area, hvacEquipment, status, searchString } = selectedFilterObject;
  // const [sensorsNum, setSensorsNum] = useState(0);
  // const [activeSensorsNum, setActiveSensorsNum] = useState(0);
  const [snackbarState, setSnackbarState] = useState({
    snackbarOpen: false,
    vertical: 'top',
    horizontal: 'center',
  });
  const { vertical, horizontal, snackbarOpen } = snackbarState;
  const [hasCompletedInitialFetch, setHasCompletedInitialFetch] = useState(false);
  const hasSeenLoadingRef = useRef(false);
  const [showEmptyState, setShowEmptyState] = useState(false);
  const initialDevicesRef = useRef(allDevices);
  const [hasReceivedFirstDevicesPayload, setHasReceivedFirstDevicesPayload] = useState(false);
  const [selectedParameterGroup, setSelectedParameterGroup] = useState('iaq');

  const handleParameterGroupChange = (_event, nextParameterGroup) => {
    if (nextParameterGroup) {
      setSelectedParameterGroup(nextParameterGroup);
    }
  };

  // Set current page for navigation tracking (safe, no dependencies)
  useEffect(() => {
    dispatch(setCurrentPage('sensors-list'));

    // Initialize data on mount (stable)
    const initializeData = async () => {
      dispatch(fetchUserDevices({ devicesType: 'Sensor' }));
    };

    initializeData();

    // Auto-refresh sensors data (stable interval)
    const interval = setInterval(() => {
      dispatch(fetchUserDevices({ devicesType: 'Sensor' }));
    }, REFRESH_TIME_IN_SECONDS * 1000);

    return () => {
      clearInterval(interval);
      dispatch(clearSearchString());
    };
  }, [dispatch]);

  useEffect(() => {
    if (isLoading) {
      hasSeenLoadingRef.current = true;
      return;
    }
    if (!isLoading && hasSeenLoadingRef.current && !hasCompletedInitialFetch) {
      setHasCompletedInitialFetch(true);
    }
  }, [isLoading, hasCompletedInitialFetch]);

  useEffect(() => {
    if (hasCompletedInitialFetch && !hasReceivedFirstDevicesPayload && allDevices !== initialDevicesRef.current) {
      setHasReceivedFirstDevicesPayload(true);
    }
  }, [allDevices, hasCompletedInitialFetch, hasReceivedFirstDevicesPayload]);

  const filteredDevices = useMemo(() => {
    if (!currentUser.role || allDevices?.length === 0) return [];
    // centralized filterDevices function directly (no manual customer filtering needed)

    const filtered = filterDevices(
      allDevices || [],
      {
        distributor,
        customer,
        building,
        floor,
        area,
        hvacEquipment,
        status,
        searchString,
      },
      currentUser,
    );

    // Sort by status (Green → Yellow → Red → Grey)
    const sorted = filtered.sort((a, b) => {
      // Define status priority order: 1 (Green) → 2 (Yellow) → 3 (Red) → 4 (Grey)
      const statusOrder = { 1: 1, 2: 2, 3: 3, 4: 4 };

      // Get priority for each device (default to 4 for unknown status)
      const priorityA = statusOrder[a.status] || 4;
      const priorityB = statusOrder[b.status] || 4;

      // Sort by priority (ascending: 1, 2, 3, 4)
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }

      // Secondary sort: alphabetical by name for same status
      return (a.name || '').localeCompare(b.name || '');
    });

    return sorted;
  }, [devices, distributor, customer, building, floor, area, hvacEquipment, status, searchString]);

  const sensorCardParams = device => {
    if (selectedParameterGroup === 'bdi-thermal') {
      return {
        'Thermal Justified': device.thermal_justified,
        'Thermal Requested': device.thermal_requested,
        'Thermal Saveable': device.thermal_saveable,
      };
    }

    if (selectedParameterGroup === 'bdi-air') {
      return {
        'Air Justified': device.air_justified,
        'Air Requested': device.air_requested,
        'Air Saveable': device.air_saveable,
      };
    }

    return {
      'Clean air': device.clean_air_level_min,
      Ventilation: device.co2_ratio_min,
      Comfort: device.comfort_level_avg,
    };
  };

  useEffect(() => {
    if (!hasCompletedInitialFetch || !hasReceivedFirstDevicesPayload || isLoading || filteredDevices?.length > 0) {
      setShowEmptyState(false);
      return;
    }

    const timer = setTimeout(() => {
      setShowEmptyState(true);
    }, 250);

    return () => clearTimeout(timer);
  }, [hasCompletedInitialFetch, hasReceivedFirstDevicesPayload, isLoading, filteredDevices]);

  // Snackbar helpers
  const handleSnackbarClose = () => {
    setSnackbarState({ ...snackbarState, snackbarOpen: false });
  };

  const getDisplayTitle = () => {
    if (building !== 'all') {
      const buildingName = devices?.find(d => d.building_id?.toString() === building)?.building;
      return `${buildingName || 'Building'} sensors list`;
    }
    return 'All sensors list';
  };

  function formatDayDdMonthYear(date, timeZone) {
    const fmt = new Intl.DateTimeFormat('en-GB', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      ...(timeZone && { timeZone }),
    });
    const parts = Object.fromEntries(fmt.formatToParts(date).map(p => [p.type, p.value]));
    return `${parts.weekday}, ${parts.day} ${parts.month} ${parts.year}`;
  }

  const renderSensorSkeletons = () =>
    Array.from({ length: SKELETON_CARDS_COUNT }).map((_, index) => (
      <Grid
        sx={{
          lineHeight: 0,
          position: 'relative',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'center',
          display: 'flex',
          justifyContent: { xs: 'center', sm: 'start' },
          boxSizing: 'border-box',
          width: { xs: '100%', sm: '19.5rem' },
          flex: '0 0 auto',
        }}
        key={`sensor-skeleton-${index}`}>
        <Box
          sx={{
            position: 'relative',
            width: { xs: '100%', sm: '19.5rem' },
            maxWidth: '19.5rem',
            aspectRatio: '576 / 382',
            overflow: 'hidden',
            filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.28))',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: 'auto',
              aspectRatio: '576 / 382',
              zIndex: 0,
              pointerEvents: 'none',
              boxShadow: 'inset -12px -12px 18px rgba(0, 0, 0, 0.24)',
              WebkitMaskImage: `url(${emptySensorImage})`,
              maskImage: `url(${emptySensorImage})`,
              WebkitMaskPosition: 'top left',
              maskPosition: 'top left',
              WebkitMaskRepeat: 'no-repeat',
              maskRepeat: 'no-repeat',
              WebkitMaskSize: '100% 100%',
              maskSize: '100% 100%',
            },
          }}>
          <Box
            component="img"
            src={emptySensorImage}
            alt=""
            aria-hidden="true"
            sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
          />
          <Box sx={{ position: 'relative', zIndex: 1, px: { xs: 2, sm: 2 }, pt: 2.25 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Skeleton variant="circular" width={30} height={30} />
              <Skeleton variant="text" width={56} height={18} />
            </Box>
            <Skeleton variant="text" width="52%" height={25} sx={{ mt: 0.6 }} />
            <Box sx={{ mt: 0.5 }}>
              <Skeleton variant="text" width="48%" height={18} />
              <Skeleton variant="text" width="30%" height={18} sx={{ mt: -0.2 }} />
            </Box>
          </Box>
          <Box
            sx={{
              position: 'absolute',
              zIndex: 1,
              right: { xs: 2, sm: 1 },
              bottom: { xs: 1.5, sm: 1.25 },
              left: { xs: 2, sm: 1 },
              display: 'flex',
              justifyContent: 'center',
              gap: 0,
            }}>
            {[0, 1, 2].map(metric => (
              <Box
                key={metric}
                sx={{
                  width: '5.7rem',
                  height: { xs: '3.5rem', sm: '4rem' },
                  pt: 1,
                  borderRadius: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'linear-gradient(to bottom, rgba(224, 247, 232, 0.82) 0%, rgba(255, 255, 255, 0) 100%)',
                }}>
                <Skeleton variant="text" width="68%" height={16} />
                <Skeleton variant="text" width="48%" height={24} sx={{ mt: -0.25 }} />
              </Box>
            ))}
          </Box>
        </Box>
      </Grid>
    ));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {/* ===== HEADER WITH COUNTS ===== */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Box ml={1}>
          <MDTypography sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' }, fontWeight: 'bold' }} color="dark">
            {getDisplayTitle()}
          </MDTypography>

          <CustomSnackbar
            open={snackbarState.snackbarOpen}
            duration={3000}
            onClose={handleSnackbarClose}
            message="Please select a distributor and a customer"
            vertical={vertical}
            horizontal={horizontal}
          />

          {<DevicesCount filteredDevices={filteredDevices || []} devicesType="sensors"></DevicesCount>}
        </Box>
        <Box
          sx={{
            display: { xs: 'none', sm: 'flex' },
            flexDirection: 'column',
            alignItems: 'center',
          }}>
          <LiveClock />
          <Typography sx={{ fontSize: '.85rem' }}>{formatDayDdMonthYear(new Date())}</Typography>
        </Box>
      </Box>

      {/* ===== MAIN CONTENT ===== */}
      <Box sx={{ display: 'flex', flexDirection: 'row' }}>
        <Box ml={0} mb={1} sx={{ width: '100%' }}>
          {/* ===== FILTER COMPONENT ===== */}
          <DevicesListFilter
            type="sensors"
            hvacEquipment
            extraFilterFields={
              <ToggleButtonGroup
                exclusive
                value={selectedParameterGroup}
                onChange={handleParameterGroupChange}
                aria-label="sensor parameter group"
                sx={{
                  height: '46px',
                  width: { xs: '100%', sm: 'calc(100% - 8px)', lg: 280 },
                  ml: { sm: 1 },
                  borderRadius: '0.375rem',
                  backgroundColor: brandColors.FILTER_BAR,
                  overflow: 'hidden',
                  '& .MuiToggleButton-root': {
                    height: '46px',
                    minHeight: '46px',
                    minWidth: 0,
                    flex: 1,
                    px: 0,
                    textTransform: 'none',
                    color: '#000',
                    fontWeight: 600,
                    border: `1px solid ${brandColors.BLUE}`,
                    '&.Mui-selected, &.Mui-selected:hover': {
                      backgroundColor: brandColors.BLUE,
                      color: '#fff',
                    },
                  },
                }}>
                <ToggleButton value="iaq" aria-label="IAQ">
                  IAQ
                </ToggleButton>
                <ToggleButton value="bdi-thermal" aria-label="BDI Thermal">
                  BDI Thermal
                </ToggleButton>
                <ToggleButton value="bdi-air" aria-label="BDI Air">
                  BDI Air
                </ToggleButton>
              </ToggleButtonGroup>
            }
            extraFilterItemWidths={[300]}
          />

          {/* ===== SENSORS GRID ===== */}
          <Grid
            className="devicesListContainer"
            sx={{
              display: 'flex',
              alignItems: 'start',
              justifyContent: 'flex-start',
              height: 'fit-content',
              maxHeight: '71vh',
              overflow: 'auto',
              width: { xs: '100%', sm: '100%' },
              flexWrap: 'wrap',
              gap: '1.5rem',
              overflowY: 'visible',
              overflowX: 'visible',
            }}
            mt={2}>
            {isLoading
              ? renderSensorSkeletons()
              : filteredDevices?.map(device => (
                  <Grid
                    sx={{
                      lineHeight: 0,
                      position: 'relative',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: 'center',
                      display: 'flex',
                      justifyContent: { xs: 'center', sm: 'start' },
                      boxSizing: 'border-box',
                      width: { xs: '100%', sm: '19.5rem' },
                      flex: '0 0 auto',
                    }}
                    key={device.id}>
                    <SensorCard
                      className="sensorCard"
                      id={device.id}
                      status={device.status}
                      name={device.name}
                      custName={device.cust_name}
                      building={device.building}
                      floorNumber={device.floor_number}
                      area_name={device.area_name}
                      lastRecordTime={device.sensor_last_reading_time}
                      params={sensorCardParams(device)}
                    />
                  </Grid>
                ))}
          </Grid>

          {/* ===== EMPTY STATE ===== */}
          {showEmptyState && (
            <Card
              className="emptyState"
              role="status"
              sx={{
                width: '100%',
                maxWidth: 520,
                mx: 'auto',
                mt: { xs: 3, sm: 5 },
                p: { xs: 2.5, sm: 3 },
                border: '1px solid',
                borderColor: 'info.light',
                borderRadius: 4,
                boxShadow: '0 12px 28px rgba(30, 136, 229, 0.10)',
                background: 'linear-gradient(135deg, rgba(227, 242, 253, 0.9), rgba(255, 255, 255, 0.96))',
                textAlign: 'center',
              }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  mx: 'auto',
                  mb: 1.5,
                  borderRadius: '50%',
                  display: 'grid',
                  placeItems: 'center',
                  color: 'info.main',
                  backgroundColor: 'rgba(33, 150, 243, 0.12)',
                }}>
                <FilterAltOffOutlinedIcon sx={{ fontSize: 30 }} />
              </Box>
              <Typography variant="h6" color="text.primary" fontWeight={700} gutterBottom>
                No matching sensors
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 360, mx: 'auto', lineHeight: 1.6 }}>
                Try adjusting the filters or search term to see more sensors.
              </Typography>
            </Card>
          )}
        </Box>
      </Box>
    </Box>
  );
};
